import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { format } from 'date-fns';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useRecordPriceMutation } from '@/api/mifid';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { IsInternalSales } from '@/App/tiles/Option/utils/IsInternalSales.tsx';
import { TraderInfos } from '@/components/TraderInfos.tsx';
import { getQuote, getRfsId, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, OptionRfs } from '@/store/option/state.ts';
import { FULL_DATE_FORMAT_TEXT } from '@/utils/date.ts';

export function OptionFooter() {
  return (
    <IsInternalSales>
      <div className="d-flex flex-between">
        <TimestampButton />
        <OptionTraderInfos />
      </div>
    </IsInternalSales>
  );
}

function TimestampButton() {
  const [recordPrice] = useRecordPriceMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { tileId, rfsId, quoteId, quoteCcy, mifid, rfsStatus } = useOptionSelector((option) => {
    const quote = getQuote(option);
    const currencyIndex = option.properties.premiumType.premiumCurrency;
    return {
      tileId: option.properties.tileId,
      rfsId: getRfsId(option),
      quoteId: quote?.quoteId,
      quoteCcy: currencyIndex === 1 ? quote?.ccy1 : quote?.ccy2,
      mifid: option.properties.mifid,
      rfsStatus: option.properties.rfs.status,
    };
  });
  const hasBeenUsed = mifid.records.length > 0;
  const isDisabled = rfsStatus !== 'PRICING';

  function handleRecordPrice() {
    if (isNotDefined(rfsId) || isNotDefined(quoteCcy) || isNotDefined(quoteId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Option', 'RFS price recorded');

    recordPrice({
      tileId,
      date: Date.now(),

      quoteId,
      rfsId,
      prices: [
        {
          bid: quoteCcy.bidAmount,
          ask: quoteCcy.askAmount,
        },
      ],
    });
  }

  return (
    <div className="d-flex gap-2">
      <Tooltip contents={<FormattedMessage id="option.footer.timestampBtn.tooltip" />}>
        <Button
          size="sm"
          purpose="primary"
          color={isDisabled || hasBeenUsed ? 'primary' : 'danger'}
          iconPosition="start"
          disabled={isDisabled}
          onClick={handleRecordPrice}
          data-e2e={'button-timestamp'}
        >
          <i className="icon icon-sm">timer</i>
          <FormattedMessage id="option.footer.timestampBtn" />
        </Button>
      </Tooltip>
      {!isDisabled && <PriceRecordsHistory />}
    </div>
  );
}

function PriceRecordsHistory() {
  const { currencyPair, mifid } = useOptionSelector((option) => {
    return {
      tileId: option.properties.tileId,
      currencyPair: selectOptionCurrency(option).currencyPair,
      mifid: option.properties.mifid,
    };
  });

  return (
    <OverlayTrigger
      rootClose
      placement="bottom"
      trigger="click"
      overlay={
        <Popover style={{ maxWidth: '300px' }}>
          <Popover.Header data-e2e="timestamp-popover-title">
            <div className="d-flex flex-column">
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.title"
                values={{ currencyPair, instrument: 'Option' }}
              />
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.subtitle"
                values={{
                  date: isDefined(mifid.rfsDate) ? format(mifid.rfsDate, FULL_DATE_FORMAT_TEXT) : '',
                }}
              />
            </div>
          </Popover.Header>

          <Popover.Body>
            <div className="d-flex flex-column gap-1" data-e2e="timestamp-popover-body">
              {mifid.records.toReversed().map((record, index) => (
                // oxlint-disable-next-line no-array-index-key -- index
                <div key={index}>
                  {record.isError ? (
                    <div className="text-danger">
                      <i className="icon icon-sm">error</i>
                      <FormattedMessage
                        id="priceRecordsHistory.popover.item.error"
                        values={{
                          item: mifid.records.length - index,
                          date: format(record.date, FULL_DATE_FORMAT_TEXT),
                        }}
                      />
                    </div>
                  ) : (
                    <FormattedMessage
                      id="priceRecordsHistory.popover.item"
                      values={{
                        item: mifid.records.length - index,
                        date: format(record.date, FULL_DATE_FORMAT_TEXT),
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {mifid.records.some((r) => r.isError) ? (
        <div className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer text-danger">
          <i className="icon icon-sm">error</i>
          <FormattedMessage id="priceRecordsHistory.recording.error" />
        </div>
      ) : (
        <div
          data-e2e="timestamp-count"
          className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer"
        >
          {mifid.records?.length ?? 0}
        </div>
      )}
    </OverlayTrigger>
  );
}

function OptionTraderInfos() {
  const traderId = useOptionSelector(getTraderId);
  const tileId = useOptionSelector((option) => option.properties.tileId);
  const contribution = useOptionSelector((option) => getContribution(option.properties.rfs));

  return <TraderInfos contribution={contribution} tileId={tileId} traderId={traderId} />;
}

function getContribution(rfs: OptionRfs): 'Automatic' | 'Manual' {
  if (rfs?.status === 'PRICING' || rfs?.status === 'EXPIRED') {
    return rfs.quote.contribution;
  }
  return 'Manual';
}

function getTraderId(option: FxOption): string | undefined {
  if (option.properties.rfs.status === 'AWAITING' || option.properties.rfs.status === 'PRICING') {
    return option.properties.rfs.traderId;
  }
}
